import * as esriLoader from 'esri-loader';
import { Subject } from 'rxjs';

import { environment } from '../environment';

const esriRequestMaxUrlLength = 1000;
const interceptedErrorSubject = new Subject<__esri.Error>();

export class Interceptor {
    public static esriError = interceptedErrorSubject.asObservable();

    private static addedInterceptorPromise: Promise<void>;
    public static async addInterceptor() {
        if (!this.addedInterceptorPromise) {
            this.addedInterceptorPromise = new Promise(async (resolve) => {
                //add interceptors to ESRI requests
                const [esriConfig] = await esriLoader.loadModules(['esri/config'], window.esriConfig) as [__esri.config];

                esriConfig.request.maxUrlLength = esriRequestMaxUrlLength;

                const interceptor: __esri.RequestInterceptor = {
                    urls: [`${environment.apiBaseUrl}/arcgis/mapserver`],
                    before: (params: { url: string; requestOptions: __esri.RequestOptions }) => {
                        // Always do work for authentication cookie
                        const cookieRow = document.cookie.split('; ').map((item) =>  item.split('=')).find((item) => item[0] === 'PDAuth_token');
                        if (cookieRow) {
                            const pdAuthToken = cookieRow[1];
                            if (!params.requestOptions.headers) {
                                params.requestOptions.headers = {
                                    'X-Auth-Token': pdAuthToken
                                };
                            } else {
                                params.requestOptions.headers['X-Auth-Token'] = pdAuthToken;
                            }
                        }
                        params.requestOptions.withCredentials = true;
                        // Deal with a bug in ESRI's code where a query to this endpoint doesn't move all the data out of the query string
                        if (params.url.includes('/mapserver/dynamicLayer') && params.url.length > esriRequestMaxUrlLength) {
                            const url = new URL(params.url);
                            url.searchParams.forEach((val, key) => {
                                params.requestOptions.query[key] = val;
                            });
                            url.search = '';
                            params.url = url.href;
                        }

                        // Deal with ESRI's theme bugs
                        if (params.url.includes('/export')
                            && params.requestOptions.query.dynamicLayers
                            && ((<string>params.requestOptions.query.dynamicLayers).includes('uniqueValueInfos')
                            || (<string>params.requestOptions.query.dynamicLayers).includes('classBreakInfos'))) {
                            const parsedLayers = JSON.parse(params.requestOptions.query.dynamicLayers) as any[];
                            let modified = false;
                            for (const layer of parsedLayers) {
                                if (layer.drawingInfo && layer.drawingInfo.renderer && layer.drawingInfo.renderer.classBreakInfos) {
                                    /* This is needed because of a bug. This should be removed if possible.
                                     * When using the numeric renderer, the first break doesn't renderer
                                     * on the map until the slider is moved. This is probably due to us
                                     * not building the renderer in the right way, but the solution eludes me.*/
                                    const mins: number[] = [];
                                    (<any[]>layer.drawingInfo.renderer.classBreakInfos).forEach(cbi => {
                                        mins.push(cbi.classMinValue as number);
                                        delete cbi.classMinValue;
                                    });
                                    const min = mins.sort((a, b) => a - b)[0];
                                    layer.drawingInfo.renderer.minValue = layer.drawingInfo.renderer.minValue || min || 0;
                                    modified = true;
                                }
                                if (layer.drawingInfo && layer.drawingInfo.renderer && layer.drawingInfo.renderer.uniqueValueInfos) {
                                    /* This is needed because of a bug. This should be removed if possible.
                                     * When a unique value is null it isn't included in the value, but
                                     * if the value is a string of null, it doesn't query properly, so
                                     * we have to replace it. */
                                    (<any[]>layer.drawingInfo.renderer.uniqueValueInfos).forEach(uvi => {
                                        if (uvi.value === undefined || (typeof uvi.value === 'string' && uvi.value.toLowerCase() === 'null')) {
                                            uvi.value = '-NULL-';
                                            modified = true;
                                        }
                                    });
                                }
                            }
                            if (modified) {
                                params.requestOptions.query.dynamicLayers = JSON.stringify(parsedLayers);
                            }
                        }
                    },
                    error: ((er: __esri.Error) => {
                        interceptedErrorSubject.next(er);
                    }) as any
                };
                esriConfig.request.interceptors.push(interceptor);

                /* This is needed because the Sketch tool makes long running count requests
                 * for Statewide extent. This dummies those requests since we only need it
                 * for snapping for the Intersection selection tool at neighborhood extents */
                const paroadsInterceptor: __esri.RequestInterceptor = {
                    urls: [`${environment.apiBaseUrl}/arcgis/paroads`],
                    before: (params: { url: string; requestOptions: __esri.RequestOptions }) => {
                        if (params.requestOptions.query.returnCountOnly &&
                            !params.requestOptions.query.geometry) {
                            params.requestOptions.query.where = 'ROWNUM <= 100';
                        }
                    },
                    error: ((er: __esri.Error) => {
                        interceptedErrorSubject.next(er);
                    }) as any
                };
                esriConfig.request.interceptors.push(paroadsInterceptor);

                //resolve finish promise
                resolve();
            });
        } else {
            //await finish promise
            await this.addedInterceptorPromise;
        }
    }
}
